import { ref } from 'vue';

/**
 * Feedback entry points.
 *
 * Two concerns live here:
 *
 * 1. The contextual nudge — a subtle, dismissible prompt in the lower-right
 *    shown only when we observe a frontend problem (a Sentry error, a 5xx, or
 *    a network failure — see the emitters in plugins/sentry.js and the axios
 *    client).
 * 2. `openFeedback()`, the one call every entry point (profile menu,
 *    contextual prompt) makes to open the in-app reporter.
 *
 * The reporter itself lives in `src/features/feedback/`. It is reached through
 * a window event rather than a direct call — exactly like UpgradeModal
 * (src/components/access/UpgradeModal.vue) — so the non-Vue emitters can trip
 * it without Pinia timing concerns, and so this module does not have to know
 * whether the launcher is mounted.
 *
 * Sentry keeps error monitoring, tracing and session replay. Its User Feedback
 * widget is deliberately NOT used: two feedback forms would mean reports
 * landing in two places, and the widget cannot be limited to the per-user
 * allowlist this capability requires.
 */

export const FEEDBACK_ISSUE_EVENT = 'feedback:issue';
export const FEEDBACK_OPEN_EVENT = 'feedback:open';

// How long the nudge stays visible before hiding itself, so it never becomes a
// permanent fixture. Reset on each new issue.
const ISSUE_TTL_MS = 30000;

const issueActive = ref(false);
const issueContext = ref(null); // { message, route } — passed through for labeling

let clearTimer = null;

function hidePrompt() {
  issueActive.value = false;
  if (clearTimer) {
    clearTimeout(clearTimer);
    clearTimer = null;
  }
}

function surfaceIssue(detail) {
  issueContext.value = detail || issueContext.value;
  issueActive.value = true;
  if (clearTimer) clearTimeout(clearTimer);
  clearTimer = setTimeout(hidePrompt, ISSUE_TTL_MS);
}

export function dismissIssue() {
  hidePrompt();
  issueContext.value = null;
}

/**
 * Ask the in-app reporter to open. The launcher ignores the request unless the
 * backend has confirmed this account may submit, so callers never need to
 * repeat the access check — but they should still hide their own entry point
 * (see `useFeedbackAccess`) rather than offer a button that does nothing.
 */
export function openFeedback(context = null) {
  if (typeof window === 'undefined') return false;
  if (context) issueContext.value = context;
  window.dispatchEvent(new CustomEvent(FEEDBACK_OPEN_EVENT, { detail: context }));
  hidePrompt();
  return true;
}

/**
 * Fire-and-forget signal usable from non-Vue modules. Kept as a window event
 * (not a direct call) so emitters don't import Vue reactivity or care whether
 * the app is mounted yet.
 */
export function signalFeedbackIssue(detail = {}) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(FEEDBACK_ISSUE_EVENT, { detail }));
}

// Bind the listener once at module load (imported at app boot via FeedbackPrompt).
// Guarded for SSR/tests where window may be absent.
if (typeof window !== 'undefined') {
  window.addEventListener(FEEDBACK_ISSUE_EVENT, (event) => surfaceIssue(event?.detail || null));
}

export function useFeedback() {
  return { issueActive, issueContext };
}
